import { gql } from '@apollo/client';

// User queries
export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      name
      email
      tokenBalance
      profileImage
      bio
      role
    }
  }
`;

export const GET_USER_GROUPS = gql`
  query GetUserGroups {
    myGroups {
      id
      name
      type
      status
      currentSize
      targetSize
      savingsRate
      product {
        id
        name
        images
        currentPrice
      }
    }
  }
`;

export const GET_USER_RESOURCES = gql`
  query GetUserResources {
    myResources {
      id
      title
      type
      status
      tokenValue
      description
    }
  }
`;

export const GET_USER_TRANSACTIONS = gql`
  query GetUserTransactions {
    myTransactions {
      id
      type
      amount
      status
      createdAt
      description
      from {
        id
        name
      }
      to {
        id
        name
      }
    }
  }
`;

// Product and group queries
export const GET_ALL_PRODUCTS = gql`
  query GetAllProducts($category: String, $limit: Int) {
    products(category: $category, limit: $limit) {
      id
      name
      description
      category
      subcategory
      images {
        full
        alt
      }
      basePrice
      currentPrice
      unit
      quantity
      available
      attributes {
        organic
        local
        sustainablySourced
      }
      vendor {
        id
        name
        location {
          city
          state
        }
      }
      priceTiers {
        groupSize
        price
      }
      groups {
        id
        currentSize
        targetSize
        status
      }
    }
  }
`;

export const GET_PRODUCT_BY_ID = gql`
  query GetProductById($id: ID!) {
    product(id: $id) {
      id
      name
      description
      category
      subcategory
      images {
        full
        thumbnail
        alt
      }
      basePrice
      currentPrice
      unit
      quantity
      available
      attributes {
        organic
        local
        sustainablySourced
      }
      vendor {
        id
        name
        location {
          city
          state
        }
        profileImage
      }
      priceTiers {
        groupSize
        price
      }
      groups {
        id
        name
        currentSize
        targetSize
        status
      }
    }
  }
`;

export const GET_FEATURED_GROUPS = gql`
  query GetFeaturedGroups($limit: Int) {
    featuredGroups(limit: $limit) {
      id
      name
      type
      status
      currentSize
      targetSize
      savingsRate
      daysLeft
      product {
        id
        name
        description
        images
        basePrice
        currentPrice
        category
        vendor {
          id
          name
          location
        }
      }
      nextPriceBreak {
        size
        price
        savingsPercentage
      }
    }
  }
`;

export const GET_GROUP_BY_ID = gql`
  query GetGroupById($id: ID!) {
    group(id: $id) {
      id
      name
      type
      status
      currentSize
      targetSize
      savingsRate
      daysLeft
      createdAt
      organizer {
        id
        name
        profileImage
      }
      product {
        id
        name
        description
        images
        basePrice
        currentPrice
        category
        unit
        vendor {
          id
          name
          location
          profileImage
        }
        priceTiers {
          groupSize
          price
        }
        attributes {
          organic
          local
          sustainablySourced
        }
        origin {
          farm
          location
        }
      }
      participants {
        id
        name
        profileImage
        joinedAt
      }
      nextPriceBreak {
        size
        price
        savingsPercentage
      }
      logistics {
        type
        pickupLocation {
          name
          address
          city
        }
        scheduledTime
      }
    }
  }
`;

// Shopping cart queries
export const GET_USER_CART = gql`
  query GetUserCart {
    myCart {
      id
      items {
        product {
          id
          name
          images
          currentPrice
          unit
          vendor {
            name
            location
          }
        }
        quantity
        totalPrice
      }
      totalAmount
      updatedAt
    }
  }
`;

// Purchase queries
export const GET_USER_PURCHASES = gql`
  query GetUserPurchases($status: PurchaseStatus) {
    myPurchases(status: $status) {
      id
      status
      totalAmount
      savingsAmount
      paymentMethod
      createdAt
      confirmedAt
      deliveredAt
      group {
        id
        name
      }
      products {
        product {
          id
          name
          images
        }
        quantity
        priceAtPurchase
        totalPrice
      }
      logistics {
        type
        status
        scheduledTime
        deliveryAddress {
          address
          city
          state
        }
      }
    }
  }
`;

export const GET_PURCHASE_BY_ID = gql`
  query GetPurchaseById($id: ID!) {
    purchase(id: $id) {
      id
      status
      totalAmount
      savingsAmount
      paymentMethod
      createdAt
      confirmedAt
      deliveredAt
      group {
        id
        name
        organizer {
          name
          email
        }
      }
      products {
        product {
          id
          name
          description
          images
          vendor {
            name
            location
          }
        }
        quantity
        priceAtPurchase
        totalPrice
      }
      logistics {
        type
        status
        scheduledTime
        assignedTo {
          id
          name
        }
        deliveryAddress {
          address
          city
          state
          zipCode
        }
        notes
      }
    }
  }
`;

// Search and filter queries
export const SEARCH_PRODUCTS = gql`
  query SearchProducts($query: String!, $filters: ProductFilters) {
    searchProducts(query: $query, filters: $filters) {
      id
      name
      description
      category
      images
      basePrice
      currentPrice
      unit
      vendor {
        name
        location
      }
      attributes {
        organic
        local
        sustainablySourced
      }
      groups {
        id
        currentSize
        targetSize
        status
      }
    }
  }
`;

export const GET_CATEGORIES = gql`
  query GetCategories {
    categories {
      name
      count
      subcategories {
        name
        count
      }
    }
  }
`;

// Analytics queries
export const GET_USER_ANALYTICS = gql`
  query GetUserAnalytics {
    userAnalytics {
      totalSavings
      totalPurchases
      totalTokensEarned
      totalTokensSpent
      currentTokenBalance
      savingsThisMonth
      savingsThisYear
      groupsJoined
      resourcesContributed
      carbonFootprintReduced
      localEconomicImpact
    }
  }
`;

// Vendor-specific queries
export const GET_VENDOR_PRODUCTS = gql`
  query GetVendorProducts {
    myProducts {
      id
      name
      description
      category
      basePrice
      currentPrice
      images {
        full  
        alt
      }
      priceTiers {
        groupSize
        price
      }
      available
      approvalStatus
      createdAt
    }
  }
`;
  

export const GET_VENDOR_PURCHASES = gql`
  query GetVendorPurchases($status: PurchaseStatus, $limit: Int, $offset: Int) {
    vendorPurchases(status: $status, limit: $limit, offset: $offset) {
      id
      status
      totalAmount
      createdAt
      confirmedAt
      deliveredAt
      products {
        product {
          id
          name
          images {
            full
          }
        }
        quantity
        priceAtPurchase
        totalPrice
      }
      logistics {
        type
        status
        scheduledTime
      }
    }
  }
`;



